import { captureEvent, withScope } from '@sentry/react';
import { isFeatureEnabled } from 'common/utils';

type ExecutiveSummaryVersion = {
  reportId: number;
  prevContent: string;
  newContent: string;
};

/**
 * Captures a version of the executive summary to Sentry as an event with attachments.
 * This provides temporary version history until a proper backend solution is implemented.
 * Includes both new and previous content for backup.
 *
 * @param version - The executive summary version data to capture
 */
export const captureExecutiveSummaryVersion = (version: ExecutiveSummaryVersion): void => {
  // Only capture if Sentry is enabled
  if (!isFeatureEnabled('SentryEnabled')) {
    return;
  }

  try {
    withScope((scope) => {
      const timestamp = new Date().toISOString();

      // Set tags for easy filtering/querying in Sentry
      scope.setTag('event_type', 'executive_summary_version');
      scope.setTag('report_id', version.reportId);

      // Strip Redux state to avoid envelope size limits
      scope.setContext('state', null);

      // Add metadata about content sizes
      scope.setContext('content_metadata', {
        prevContentLength: version.prevContent.length,
        newContentLength: version.newContent.length,
        changeSize: version.newContent.length - version.prevContent.length,
      });

      // Add attachments for previous content and new content
      scope.addAttachment({
        filename: `executive-summary-${version.reportId}-${timestamp}-prev.md`,
        data: version.prevContent,
        contentType: 'text/plain',
      });

      scope.addAttachment({
        filename: `executive-summary-${version.reportId}-${timestamp}-new.md`,
        data: version.newContent,
        contentType: 'text/plain',
      });

      // Capture the event with highest priority to avoid dropping when quota is exceeded
      captureEvent({
        message: `Executive Summary Update - Report ${version.reportId} - ${timestamp}`,
        level: 'error',
      });
    });
  } catch (error) {
    // Silently fail - we don't want version history capture to break the update flow
    console.error('Failed to capture executive summary version to Sentry:', error);
  }
};
